// src/modules/marketplace/store/krakenApi.ts

import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react"
import { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions"
import {
  RootState,
  dynamicMiddlewaresInstance,
  registerReducer,
} from "@core/store"
import { baseQueryWithReauth } from "@/modules/app/store"
import { Kraken } from "../interfaces"

// ─── Base URL ─────────────────────────────────────────────────────────────
const rawBase = new URL(
  import.meta.env.REACT_APP_ADV_API,
  window.location.origin,
).toString()
const baseUrl = rawBase.endsWith("/") ? rawBase : rawBase + "/"

// ─── Tag types ────────────────────────────────────────────────────────────
const tagTypes = [
  "KrakenCities",
  "KrakenOverview",
  "KrakenCitiesTimeseries",
] as const

const reducerPath = "krakenApi"

// ─── Base query with auth ─────────────────────────────────────────────────
const baseQuery: BaseQueryFn = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token
    if (token) headers.set("authorization", `Bearer ${token}`)
    return headers
  },
})

// ─── Helper: serialize query params (skips null/undefined) ────────────────
function serializeParams(queryParams: Record<string, unknown>): string {
  return Object.entries(queryParams)
    .filter(([, value]) => value != null)
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
    .join("&")
}

type ApiBuilder = EndpointBuilder<
  typeof baseQuery,
  (typeof tagTypes)[number],
  typeof reducerPath
>

const endpoints = (build: ApiBuilder) => ({
  krakenCities: build.query<Kraken.CitiesResponse, Kraken.CitiesReq>({
    query: ({ queryParams }) => ({
      url: `kraken/treasures/cities`,
      method: "GET",
      params: serializeParams(queryParams as Record<string, unknown>),
    }),
    providesTags: ["KrakenCities"],
  }),

  krakenOverview: build.query<Kraken.OverviewResponse, Kraken.OverviewReq>({
    query: ({ queryParams }) => ({
      url: `kraken/treasures/overview`,
      method: "GET",
      params: serializeParams(queryParams as Record<string, unknown>),
    }),
    providesTags: ["KrakenOverview"],
  }),

  krakenCitiesTimeseries: build.query<
    Kraken.CitiesTimeseriesResponse,
    Kraken.CitiesTimeseriesReq
  >({
    query: ({ queryParams }) => ({
      url: `kraken/treasures/cities/timeseries`,
      method: "GET",
      params: serializeParams(queryParams as Record<string, unknown>),
    }),
    providesTags: ["KrakenCitiesTimeseries"],
  }),
})

export const krakenApi = createApi({
  reducerPath,
  tagTypes,
  baseQuery: baseQueryWithReauth(baseQuery),
  endpoints: (build) => ({ ...endpoints(build) }),
})

dynamicMiddlewaresInstance.addMiddleware(krakenApi.middleware)
registerReducer(krakenApi.reducerPath, krakenApi.reducer)

export const {
  useKrakenCitiesQuery,
  useKrakenOverviewQuery,
  useKrakenCitiesTimeseriesQuery,
} = krakenApi
